import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  Ellipsis,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  TriangleAlert,
  Upload,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  Input,
  Select,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
  badgeStyles,
  cn,
  type SortDirection,
} from '@/components/ui';
import {
  WEIGHT_OPTIONS,
  getWeightStatus,
  hasResidual,
  isFixed,
  isFree,
  isIgnored,
  isInFit,
} from '@/logic/weights';
import { formatFixed } from '@/logic/statistics';
import { isCoordinate } from '@/utils/fileParser';
import type { PointPair, TransformationSolution } from '@/types';
import { WeightMenu } from './WeightMenu';

type SortKey = 'record' | 'field' | 'weight' | 'residual';

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'In fit (Unity, Fixed)' },
  { value: 'unity', label: 'Unity' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'free', label: 'Free' },
  { value: 'ignore', label: 'Ignore' },
  { value: 'flagged', label: 'Flagged for review' },
  { value: 'invalid', label: 'Incomplete or invalid' },
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

const isStatusFilter = (value: string): value is StatusFilter =>
  STATUS_FILTERS.some((filter) => filter.value === value);

const compareIds = (left: string, right: string) =>
  left.localeCompare(right, undefined, { numeric: true });

const weightRank = (weight: number) =>
  WEIGHT_OPTIONS.findIndex(
    (option) => option.status === getWeightStatus(weight)
  );

const isInvalidPair = (pair: PointPair) =>
  [pair.n1, pair.e1, pair.n2, pair.e2].some((value) => !isCoordinate(value));

const residualValue = (
  pair: PointPair,
  residuals: Map<number, TransformationSolution['residuals'][number]>
): number | null =>
  hasResidual(pair.weight) ? (residuals.get(pair.id)?.total ?? null) : null;

export interface PointTableProps {
  pointPairs: PointPair[];
  solution: TransformationSolution | null;
  solutionStale: boolean;
  trialSolution?: TransformationSolution | null;
  selectedPointId: number | null;
  flaggedPointIds: readonly number[];
  /** Pair files need both coordinate sets for their point lookups. */
  canImportPairs: boolean;
  onSelectPoint: (id: number) => void;
  onEditPair: (id: number) => void;
  onSetWeight: (id: number, weight: number) => void;
  onAddPair: () => void;
  onResetWeights: () => void;
  onImportPairs: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Shown in place of the table when there are no pairs. */
  emptyState?: ReactNode;
}

/**
 * The shared review table: correspondence, weight, accepted (and trial)
 * residual, and current review flags. Rows select the pair shared with the
 * map; a pair selected elsewhere is revealed even when filters hide it.
 */
export const PointTable = ({
  pointPairs,
  solution,
  solutionStale,
  trialSolution,
  selectedPointId,
  flaggedPointIds,
  canImportPairs,
  onSelectPoint,
  onEditPair,
  onSetWeight,
  onAddPair,
  onResetWeights,
  onImportPairs,
  emptyState,
}: PointTableProps) => {
  const pairFileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectButtons = useRef(new Map<number, HTMLButtonElement>());
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDirection }>({
    key: 'record',
    dir: 'asc',
  });

  const residuals = useMemo(
    () => new Map(solution?.residuals.map((r) => [r.id, r])),
    [solution]
  );
  const trialResiduals = useMemo(
    () => new Map(trialSolution?.residuals.map((r) => [r.id, r])),
    [trialSolution]
  );
  const flagged = useMemo(() => new Set(flaggedPointIds), [flaggedPointIds]);

  const matchesFilters = (pair: PointPair) => {
    const text =
      `${pair.label} ${pair.sys1Point} ${pair.sys2Point} ${pair.desc1 ?? ''} ${pair.desc2 ?? ''}`.toLowerCase();
    if (!text.includes(query.trim().toLowerCase())) return false;
    switch (statusFilter) {
      case 'all':
        return true;
      case 'active':
        return isInFit(pair.weight);
      case 'unity':
        return getWeightStatus(pair.weight) === 'unity';
      case 'fixed':
        return isFixed(pair.weight);
      case 'free':
        return isFree(pair.weight);
      case 'ignore':
        return isIgnored(pair.weight);
      case 'flagged':
        return flagged.has(pair.id);
      case 'invalid':
        return isInvalidPair(pair);
    }
  };

  const selectedPair = pointPairs.find((pair) => pair.id === selectedPointId);
  const revealingSelected = selectedPair && !matchesFilters(selectedPair);
  const visiblePairs = pointPairs
    .filter((pair) => matchesFilters(pair) || pair.id === selectedPointId)
    .sort((left, right) => {
      const direction = sort.dir === 'asc' ? 1 : -1;
      if (sort.key === 'residual') {
        const a = residualValue(left, residuals);
        const b = residualValue(right, residuals);
        // Pairs without a residual stay last in either direction.
        if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
        return (a - b) * direction;
      }
      if (sort.key === 'weight')
        return (weightRank(left.weight) - weightRank(right.weight)) * direction;
      if (sort.key === 'field')
        return compareIds(left.sys2Point, right.sys2Point) * direction;
      return compareIds(left.sys1Point, right.sys1Point) * direction;
    });

  // One tab stop for the row buttons: the selected row, else the first.
  const selectedVisible = visiblePairs.some(
    (pair) => pair.id === selectedPointId
  )
    ? selectedPointId
    : null;
  const largestShown = Math.max(
    0,
    ...visiblePairs.map((pair) => residualValue(pair, residuals) ?? 0)
  );
  const activeCount = pointPairs.filter(
    (pair) => isInFit(pair.weight) && !isInvalidPair(pair)
  ).length;
  const allUnity = pointPairs.every(
    (pair) => getWeightStatus(pair.weight) === 'unity'
  );

  // Keep the selected row visible by scrolling only the table, preserving
  // map keyboard focus and the page position.
  useEffect(() => {
    // The Table primitive's wrapper is the scrolling element.
    const container = scrollRef.current?.firstElementChild;
    if (!(container instanceof HTMLElement)) return;
    const row = container.querySelector<HTMLElement>(
      `[data-pair-id="${selectedPointId}"]`
    );
    if (!row) return;
    const bounds = container.getBoundingClientRect();
    const rowBounds = row.getBoundingClientRect();
    const headerHeight =
      container.querySelector('thead')?.getBoundingClientRect().height ?? 0;
    if (rowBounds.top < bounds.top + headerHeight) {
      container.scrollTop += rowBounds.top - bounds.top - headerHeight;
    } else if (rowBounds.bottom > bounds.bottom) {
      container.scrollTop += rowBounds.bottom - bounds.bottom;
    }
  }, [selectedPointId, query, statusFilter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'residual' ? 'desc' : 'asc' }
    );
  const sortDirection = (key: SortKey) => sort.key === key && sort.dir;

  const moveSelection = (event: KeyboardEvent, index: number) => {
    const offsets: Record<string, number> = {
      ArrowDown: 1,
      ArrowUp: -1,
      Home: -index,
      End: visiblePairs.length - 1 - index,
    };
    const offset = offsets[event.key];
    if (offset === undefined) return;
    const next = visiblePairs[index + offset];
    event.preventDefault();
    if (!next) return;
    onSelectPoint(next.id);
    selectButtons.current.get(next.id)?.focus();
  };

  const selectFromRow = (
    event: MouseEvent<HTMLTableRowElement>,
    id: number
  ) => {
    const target = event.target;
    // Ignore clicks on controls and on portalled menus rendered by the row.
    if (
      !(target instanceof Element) ||
      !event.currentTarget.contains(target) ||
      target.closest('button, a, input, select, [role^="menuitem"]')
    )
      return;
    onSelectPoint(id);
  };

  const isEmpty = pointPairs.length === 0 && emptyState !== undefined;
  const residualHeader = solutionStale ? 'Previous residual' : 'Residual';

  return (
    <Card
      role="region"
      aria-label="Adjustment point table"
      className="min-w-0 overflow-hidden"
    >
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-5">
        <div className="mr-1 flex min-w-0 flex-1 items-baseline gap-2 sm:flex-none">
          <h2 className="whitespace-nowrap text-base font-semibold text-fg">
            Point pairs
          </h2>
          <span className="whitespace-nowrap text-xs text-fg-muted">
            {activeCount} in fit
            <span className="hidden sm:inline">
              {' '}
              · {pointPairs.length} total
            </span>
          </span>
        </div>
        {isEmpty ? null : (
          <>
            <div className="order-3 flex min-w-0 basis-full items-center gap-2 sm:order-none sm:flex-1 sm:basis-auto">
              <Input
                type="search"
                controlSize="sm"
                leadingIcon={<Search />}
                placeholder="Search pairs"
                aria-label="Search pairs"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                containerClassName="min-w-0 flex-1 sm:max-w-60"
              />
              <Select
                controlSize="sm"
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(event) => {
                  if (isStatusFilter(event.target.value))
                    setStatusFilter(event.target.value);
                }}
                containerClassName="w-32 shrink-0 sm:w-44"
              >
                {STATUS_FILTERS.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex items-center gap-1 sm:ml-auto">
              <Button size="sm" leadingIcon={<Plus />} onClick={onAddPair}>
                Add pair
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton
                    size="sm"
                    aria-label="More pair actions"
                    icon={<Ellipsis />}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuItem
                    icon={<Upload />}
                    disabled={!canImportPairs}
                    disabledReason="Import Record and Field coordinates first"
                    onSelect={() => pairFileRef.current?.click()}
                  >
                    Import pairs…
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    icon={<RotateCcw />}
                    disabled={allUnity}
                    disabledReason="Every pair is already Unity"
                    onSelect={onResetWeights}
                  >
                    Reset all weights to Unity
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <input
                ref={pairFileRef}
                type="file"
                accept=".csv,.txt,.dat"
                aria-label="Pair file"
                className="hidden"
                onChange={(event) => {
                  onImportPairs(event);
                  event.target.value = '';
                }}
              />
            </div>
          </>
        )}
      </div>
      {revealingSelected && (
        <p
          role="status"
          className="border-t border-border bg-accent-muted px-4 py-2 text-xs text-accent sm:px-5"
        >
          Selected pair {selectedPair.label} is shown outside the current
          filters.
        </p>
      )}
      {isEmpty ? (
        <div className="border-t border-border">{emptyState}</div>
      ) : (
        <div ref={scrollRef} className="border-t border-border">
          <Table
            stickyHeader
            aria-label="Point pairs"
            containerClassName="max-h-[min(40rem,72vh)]"
          >
            <THead>
              <Tr>
                <Th
                  className="pl-4 pr-2 sm:pl-5 sm:pr-3"
                  sortDirection={sortDirection('record')}
                  onSort={() => toggleSort('record')}
                >
                  Record
                </Th>
                <Th
                  className="hidden sm:table-cell"
                  sortDirection={sortDirection('field')}
                  onSort={() => toggleSort('field')}
                >
                  Field
                </Th>
                <Th
                  className="px-2 sm:px-3"
                  sortDirection={sortDirection('weight')}
                  onSort={() => toggleSort('weight')}
                >
                  Weight
                </Th>
                <Th
                  numeric
                  className="px-2 sm:px-3"
                  sortDirection={sortDirection('residual')}
                  onSort={() => toggleSort('residual')}
                >
                  {residualHeader}
                </Th>
                {trialSolution && (
                  <Th numeric className="px-2 sm:px-3">
                    Trial
                  </Th>
                )}
                <Th className="px-2 sm:px-3">
                  <span className="sr-only sm:not-sr-only">Review</span>
                </Th>
                <Th className="w-0 pr-3 sm:pr-4">
                  <span className="sr-only">Pair details</span>
                </Th>
              </Tr>
            </THead>
            <TBody>
              {visiblePairs.map((pair, index) => {
                const status = getWeightStatus(pair.weight);
                const isSelected = pair.id === selectedPointId;
                const residual = hasResidual(pair.weight)
                  ? residuals.get(pair.id)
                  : undefined;
                const trial = hasResidual(pair.weight)
                  ? trialResiduals.get(pair.id)
                  : undefined;
                const value = residual?.total ?? null;
                const excluded = !isInFit(pair.weight);
                const invalid = isInvalidPair(pair);
                return (
                  <Tr
                    key={pair.id}
                    data-pair-id={pair.id}
                    interactive
                    selected={isSelected}
                    aria-selected={isSelected}
                    onClick={(event) => selectFromRow(event, pair.id)}
                  >
                    <Td className="pl-4 pr-2 sm:pl-5 sm:pr-3">
                      <button
                        ref={(node) => {
                          if (node) selectButtons.current.set(pair.id, node);
                          else selectButtons.current.delete(pair.id);
                        }}
                        type="button"
                        aria-label={`Select pair ${pair.label}`}
                        aria-pressed={isSelected}
                        tabIndex={
                          pair.id === (selectedVisible ?? visiblePairs[0]?.id)
                            ? 0
                            : -1
                        }
                        onClick={() => onSelectPoint(pair.id)}
                        onKeyDown={(event) => moveSelection(event, index)}
                        className={cn(
                          '-mx-1 flex min-w-0 flex-col items-start rounded px-1 text-left font-mono',
                          status === 'ignore' && 'opacity-70'
                        )}
                      >
                        <span className="font-medium text-record">
                          {pair.sys1Point || '—'}
                        </span>
                        <span className="text-xs text-field sm:hidden">
                          → {pair.sys2Point || '—'}
                        </span>
                      </button>
                    </Td>
                    <Td
                      mono
                      className={cn(
                        'hidden text-field sm:table-cell',
                        status === 'ignore' && 'opacity-70'
                      )}
                    >
                      {pair.sys2Point || '—'}
                    </Td>
                    <Td className="px-2 sm:px-3">
                      <WeightMenu
                        weight={pair.weight}
                        pairLabel={pair.label}
                        onSetWeight={(weight) => onSetWeight(pair.id, weight)}
                      />
                    </Td>
                    <Td
                      numeric
                      className={cn(
                        'px-2 sm:px-3',
                        (excluded || solutionStale) && 'text-fg-muted'
                      )}
                      title={
                        residual?.vn != null && residual.ve != null
                          ? `North ${formatFixed(residual.vn, 3, { signed: true })} · East ${formatFixed(residual.ve, 3, { signed: true })}`
                          : undefined
                      }
                    >
                      <span className="flex items-center justify-end gap-2">
                        {value !== null && largestShown > 0 && (
                          <span
                            aria-hidden="true"
                            className="hidden h-1 w-10 overflow-hidden rounded-full bg-surface-sunken sm:block"
                          >
                            <span
                              className={cn(
                                'block h-full rounded-full',
                                excluded ? 'bg-fg-subtle/60' : 'bg-residual/70'
                              )}
                              style={{
                                width: `${Math.max(4, (value / largestShown) * 100)}%`,
                              }}
                            />
                          </span>
                        )}
                        <span>{formatFixed(value, 3)}</span>
                      </span>
                    </Td>
                    {trialSolution && (
                      <Td numeric className="px-2 sm:px-3">
                        {formatFixed(trial?.total, 3)}
                      </Td>
                    )}
                    <Td className="px-2 sm:px-3">
                      {invalid ? (
                        <Badge
                          tone="warning"
                          icon={<TriangleAlert aria-hidden="true" />}
                          title="Check coordinates"
                        >
                          <span className="sr-only sm:not-sr-only">
                            Check coordinates
                          </span>
                        </Badge>
                      ) : flagged.has(pair.id) ? (
                        <button
                          type="button"
                          aria-label={`Review flagged pair ${pair.label}`}
                          onClick={() => onSelectPoint(pair.id)}
                          className={badgeStyles({
                            tone: 'danger',
                            interactive: true,
                          })}
                        >
                          <span
                            aria-hidden="true"
                            className="size-1.5 rounded-full bg-danger"
                          />
                          <span className="sr-only sm:not-sr-only">
                            Flagged
                          </span>
                        </button>
                      ) : null}
                    </Td>
                    <Td className="w-0 pr-3 sm:pr-4">
                      <IconButton
                        size="sm"
                        aria-label={`Edit pair ${pair.label}`}
                        tooltip="Edit coordinates or match"
                        icon={<Pencil />}
                        onClick={() => {
                          onSelectPoint(pair.id);
                          onEditPair(pair.id);
                        }}
                      />
                    </Td>
                  </Tr>
                );
              })}
              {visiblePairs.length === 0 && (
                <tr>
                  <td
                    colSpan={trialSolution ? 7 : 6}
                    className="px-4 py-10 text-center text-sm text-fg-muted"
                  >
                    No pairs match these filters.
                  </td>
                </tr>
              )}
            </TBody>
          </Table>
        </div>
      )}
    </Card>
  );
};
